import React, { useState, useEffect } from 'react';
import avevaApi from '@services/avevaApiService';

interface AvevaStreamDetailsProps {
  streamId: string;
  onBack: () => void;
}

interface StreamInfo {
  Id: string;
  TypeId?: string;
  Name?: string;
  Description?: string;
}

interface StreamDataItem {
  Time?: string;
  Timestamp?: string;
  Value?: any;
  Measurement?: any;
  Quality?: string;
}

const AvevaStreamDetails = ({ streamId, onBack }: AvevaStreamDetailsProps) => {
  const [streamInfo, setStreamInfo] = useState<StreamInfo | null>(null);
  const [streamData, setStreamData] = useState<StreamDataItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [activeDataTab, setActiveDataTab] = useState<'latest' | 'historical'>('latest');

  useEffect(() => {
    loadStreamDetails();
  }, [streamId]);

  const loadStreamDetails = async () => {
    setLoading(true);
    try {
      const [streamResponse, latestResponse] = await Promise.all([
        avevaApi.getStream(streamId),
        avevaApi.getLatestValue(streamId),
      ]);
      setStreamInfo(streamResponse.data);
      setStreamData([latestResponse.data]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadHistoricalData = async () => {
    setDataLoading(true);
    try {
      const endTime = new Date().toISOString();
      const startTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const response = await avevaApi.getStreamData(streamId, {
        startIndex: startTime,
        endIndex: endTime,
        count: 100,
      });
      setStreamData(response.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDataLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading stream details...</div>;

  if (error)
    return (
      <div className="stream-details">
        <button className="back-btn" onClick={onBack}>
          ← Back
        </button>
        <div className="error">Error: {error}</div>
      </div>
    );

  return (
    <div className="stream-details">
      <div className="details-header">
        <button className="back-btn" onClick={onBack}>
          ← Back
        </button>
        <h2>Stream Details: {streamId}</h2>
      </div>

      {streamInfo && (
        <div className="stream-info">
          <h3>Stream Information</h3>
          <div className="info-grid">
            <div>
              <strong>ID:</strong> {streamInfo.Id}
            </div>
            <div>
              <strong>Type:</strong> {streamInfo.TypeId || 'N/A'}
            </div>
            <div>
              <strong>Name:</strong> {streamInfo.Name || 'N/A'}
            </div>
            <div>
              <strong>Description:</strong> {streamInfo.Description || 'N/A'}
            </div>
          </div>
        </div>
      )}

      <div className="stream-data">
        <div className="data-header">
          <h3>Stream Data</h3>
          <div className="data-tabs">
            <button
              className={`tab-btn ${activeDataTab === 'latest' ? 'active' : ''}`}
              onClick={() => setActiveDataTab('latest')}
            >
              Latest Value
            </button>
            <button
              className={`tab-btn ${activeDataTab === 'historical' ? 'active' : ''}`}
              onClick={() => {
                setActiveDataTab('historical');
                if (streamData.length <= 1) loadHistoricalData();
              }}
            >
              Historical Data
            </button>
          </div>
        </div>

        {dataLoading && <div className="loading">Loading data...</div>}

        {!dataLoading && streamData.length > 0 && (
          <div className="data-content">
            {activeDataTab === 'latest' ? (
              <pre>{JSON.stringify(streamData[0], null, 2)}</pre>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Value</th>
                    <th>Quality</th>
                  </tr>
                </thead>
                <tbody>
                  {streamData.map((item) => {
                    const time = item.Time || item.Timestamp || '';
                    const value = item.Value || item.Measurement || '';

                    const key = `${time}-${value}`;

                    return (
                      <tr key={key}>
                        <td>{new Date(time).toLocaleString()}</td>
                        <td>{JSON.stringify(value || 'N/A')}</td>
                        <td>{item.Quality || 'N/A'}</td>
                      </tr>
                    );
                  })}

                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="data-actions">
          <button onClick={loadStreamDetails}>Refresh Latest</button>
          <button onClick={loadHistoricalData}>Load Historical</button>
        </div>
      </div>
    </div>
  );
};

export default AvevaStreamDetails;
